import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
} from "../../../design-system";
import {
  createContract,
  getHolidayGroups,
  getVacationPolicies,
  getWorkSchedules,
} from "../../../helpers/kronaly_helper";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const initial = () => ({
  start_date: todayYmd(),
  end_date: "",
  work_schedule_id: "",
  holiday_group_ids: [],
  vacation_policy_id: "",
  job_title: "",
  salary_amount: "",
  salary_currency: "EUR",
});

const NewContractModal = ({ open, employeeProfileId, onClose, onCreated }) => {
  const [form, setForm] = useState(initial);
  const [schedules, setSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(initial());
    setError(null);
    setLoadingCatalogs(true);
    Promise.all([getWorkSchedules(), getHolidayGroups(), getVacationPolicies()])
      .then(([a, b, c]) => {
        setSchedules(a?.data ?? a ?? []);
        setHolidays(b?.data ?? b ?? []);
        setVacations(c?.data ?? c ?? []);
      })
      .catch((e) =>
        setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar catálogos."),
      )
      .finally(() => setLoadingCatalogs(false));
  }, [open]);

  const handle = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        employee_profile_id: employeeProfileId,
        start_date: form.start_date,
        end_date: form.end_date || null,
        work_schedule_id: form.work_schedule_id,
        holiday_group_ids: form.holiday_group_ids,
        vacation_policy_id: form.vacation_policy_id,
        job_title: form.job_title.trim() || null,
        salary_amount: form.salary_amount === "" ? null : Number(form.salary_amount),
        salary_currency: form.salary_currency || "EUR",
      };
      const r = await createContract(payload);
      onCreated(r?.data ?? r);
      onClose();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo crear el contrato.");
    } finally {
      setSaving(false);
    }
  };

  const noCatalogs =
    !loadingCatalogs &&
    (schedules.length === 0 || holidays.length === 0 || vacations.length === 0);

  return (
    <Modal open={open} onClose={onClose} title="Nuevo contrato" size="lg">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}
          {noCatalogs && (
            <div className="kr-mb-3">
              <Alert tone="warning">
                Faltan catálogos. Crea al menos un horario, un grupo de festivos y una política
                de vacaciones en <a href="/configuracion" style={{ color: "inherit", textDecoration: "underline" }}>Configuración › Catálogos</a> antes de crear contratos.
              </Alert>
            </div>
          )}
          {loadingCatalogs ? (
            <div className="kr-text-center kr-py-3"><Spinner size={16} /></div>
          ) : (
            <>
              <div className="kr-grid kr-gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Fecha de inicio" required>
                  <Input type="date" value={form.start_date} onChange={handle("start_date")} required />
                </Field>
                <Field label="Fecha fin (opcional, indefinido si vacío)">
                  <Input type="date" value={form.end_date} onChange={handle("end_date")} />
                </Field>
              </div>
              <div className="kr-grid kr-gap-3 kr-mt-3" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                <Field label="Horario" required>
                  <Select value={form.work_schedule_id} onChange={handle("work_schedule_id")} required>
                    <option value="">— elige —</option>
                    {schedules.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({Math.round(s.weekly_minutes / 60)}h/sem)
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Grupos de festivos" required help="Mantén Ctrl/⌘ para seleccionar varios.">
                  <select
                    multiple
                    value={form.holiday_group_ids}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                      setForm((f) => ({ ...f, holiday_group_ids: selected }));
                    }}
                    required
                    style={{
                      width: "100%",
                      minHeight: 80,
                      border: "1px solid var(--kr-border)",
                      borderRadius: 6,
                      padding: 8,
                      font: "400 13px/1.4 var(--kr-font-sans)",
                      color: "var(--kr-fg-strong)",
                      background: "var(--kr-bg-surface)",
                    }}
                  >
                    {holidays.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}{h.kind && h.kind !== "other" ? ` (${h.kind})` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Política de vacaciones" required>
                  <Select value={form.vacation_policy_id} onChange={handle("vacation_policy_id")} required>
                    <option value="">— elige —</option>
                    {vacations.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="kr-grid kr-gap-3 kr-mt-3" style={{ gridTemplateColumns: "1fr 140px 100px" }}>
                <Field label="Puesto / cargo">
                  <Input type="text" value={form.job_title} onChange={handle("job_title")} maxLength={120} />
                </Field>
                <Field label="Salario anual">
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.salary_amount}
                    onChange={handle("salary_amount")}
                  />
                </Field>
                <Field label="Moneda">
                  <Input
                    type="text"
                    value={form.salary_currency}
                    onChange={handle("salary_currency")}
                    maxLength={3}
                  />
                </Field>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving || loadingCatalogs || noCatalogs}>
            {saving && <Spinner size={12} color="#fff" />}
            Crear contrato
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default NewContractModal;
